import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getWolAgentListApi } from '~/features/wol-agent/api/get-wol-agent-list.api';
import { getWolAgentStatusApi } from '~/features/wol-agent/api/get-wol-agent-status.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type {
  WolAgent,
  WolAgentInfoDto,
  WolAgentListParams,
  WolAgentStatusDto,
} from '~/features/wol-agent/types/wol-agent.type';

export interface WolAgentState {
  isLoading: boolean;
  list: WolAgent[];
  meta: PaginationMeta | null;
  params: WolAgentListParams | null;
  selectedWolAgent: WolAgent | WolAgentInfoDto | null;
  /** 실시간 상태 (폴링) */
  agentStatus: WolAgentStatusDto | null;
  isStatusLoading: boolean;
}

const initialState: WolAgentState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedWolAgent: null,
  agentStatus: null,
  isStatusLoading: false,
};

/**
 * WOL Agent 목록 조회
 */
export const fetchWolAgentList = createAsyncThunk<
  PaginatedApiResponse<WolAgent>,
  WolAgentListParams | undefined
>('wolAgent/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getWolAgentListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * WOL Agent 실시간 상태 조회 (storeId 기반)
 */
export const fetchWolAgentStatus = createAsyncThunk<WolAgentStatusDto, number>(
  'wolAgent/fetchStatus',
  async (storeId, { rejectWithValue }) => {
    try {
      const { data } = await getWolAgentStatusApi(storeId);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const WolAgentSlice = createSlice({
  initialState,
  name: 'wolAgent',
  reducers: {
    setSelectedWolAgent: (state, action) => {
      state.selectedWolAgent = action.payload;
    },
    resetSelectedWolAgent: state => {
      state.selectedWolAgent = null;
    },
    resetWolAgent: state => {
      state.list = [];
      state.meta = null;
      state.params = null;
      state.selectedWolAgent = null;
      state.agentStatus = null;
      state.isLoading = false;
      state.isStatusLoading = false;
    },
    resetAgentStatus: state => {
      state.agentStatus = null;
      state.isStatusLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchWolAgentList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchWolAgentList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchWolAgentList.rejected, state => {
        state.isLoading = false;
      })
      .addCase(fetchWolAgentStatus.pending, state => {
        state.isStatusLoading = true;
      })
      .addCase(fetchWolAgentStatus.fulfilled, (state, action) => {
        state.agentStatus = action.payload;
        state.isStatusLoading = false;
      })
      .addCase(fetchWolAgentStatus.rejected, state => {
        state.isStatusLoading = false;
      });
  },
});

export const { setSelectedWolAgent, resetSelectedWolAgent, resetWolAgent, resetAgentStatus } =
  WolAgentSlice.actions;
export default WolAgentSlice;
